import { useMemo } from 'react';
import { buildSparklineData } from './sparkline.js';

/**
 * Decorative balance-trend background for the account summary header body.
 * Renders nothing when the account lacks enough balance history.
 * @param {{
 *   account: { accountType: string },
 *   history?: Array<{ date: string, value: number }>,
 *   valuations?: Array<{ date: string, value: number }>,
 * }} props
 * @returns {React.ReactElement | null}
 */
export function BalanceSparkline({ account, history = [], valuations = [] }) {
	const data = useMemo(
		() => buildSparklineData(account.accountType, history, valuations),
		[account.accountType, history, valuations],
	);

	if (!data) { return null; }

	if (data.kind === 'single') {
		return (
			<svg className="spark" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
				<path className="spark-area" d={data.area} />
				{data.belowArea ? <path className="spark-band-loss" d={data.belowArea} /> : null}
				{data.zeroLine ? <path className="spark-zero-line" d={data.zeroLine} /> : null}
				<path className="spark-line" d={data.line} />
			</svg>
		);
	}

	return (
		<svg className="spark" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
			<path className="spark-band-principal" d={data.principalArea} />
			{data.profit.map((d) => <path key={d} className="spark-band-profit" d={d} />)}
			{data.loss.map((d) => <path key={d} className="spark-band-loss" d={d} />)}
			{data.zeroLine ? <path className="spark-zero-line" d={data.zeroLine} /> : null}
			<g className="two-band">
				<path className="spark-line-principal" d={data.principalLine} />
				<path className="spark-line" d={data.balanceLine} />
			</g>
		</svg>
	);
}
